import React, { useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Activity, CalendarCheck, LineChart, Camera, Dumbbell, Sparkles,
  ChevronRight, Menu, X, Zap, Gauge,
} from "lucide-react";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { Logo } from "@/components/Shared";
import { PhoneMockup } from "@/components/landing/PhoneMockup";
import { AppPreview } from "@/components/landing/AppPreview";
import { GRID_BG, fadeUp, SectionShell, SectionHead, LandingFooter, useDarkBody } from "@/components/landing/LandingUI";
import { useI18n } from "@/context/I18nContext";
import { useAuth } from "@/context/AuthContext";

const isInstalledApp = () => window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone
  || new URLSearchParams(window.location.search).get("source") === "pwa";

function PrimaryCta({ children, testid, className = "" }) {
  return (
    <Link to="/register" data-testid={testid}
      className={`group inline-flex items-center justify-center gap-2 rounded-xl bg-emerald-400 px-6 py-3.5 font-bold text-slate-950 transition-[transform,background-color,box-shadow] duration-200 hover:scale-105 hover:bg-emerald-300 hover:shadow-lg hover:shadow-emerald-400/25 ${className}`}>
      {children} <ChevronRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5" />
    </Link>
  );
}

function NavBar({ t, lang, setLang }) {
  const [open, setOpen] = useState(false);
  const links = [["#features", t("features")], ["#how", t("howItWorks")], ["#faq", t("faq")]];
  return (
    <header className="sticky top-0 z-50 border-b border-slate-800/80 bg-slate-950/80 backdrop-blur-xl">
      <div className="mx-auto flex max-w-7xl items-center justify-between px-5 py-3.5">
        <Logo className="text-white" />
        <nav className="hidden items-center gap-8 md:flex">
          {links.map(([href, label]) => (
            <a key={href} href={href} className="text-sm text-slate-400 transition-colors hover:text-emerald-400">{label}</a>
          ))}
        </nav>
        <div className="flex items-center gap-2">
          <button data-testid="lang-toggle" onClick={() => setLang(lang === "id" ? "en" : "id")}
            className="rounded-lg border border-slate-700 px-2.5 py-1.5 text-xs font-semibold text-slate-200 transition-colors hover:border-emerald-400/50 hover:text-white">
            {lang === "id" ? "EN" : "ID"}
          </button>
          <Link to="/login" data-testid="nav-login-btn" className="hidden rounded-lg px-3 py-1.5 text-sm font-medium text-slate-300 transition-colors hover:text-white md:block">{t("login")}</Link>
          <Link to="/register" data-testid="nav-register-btn" className="hidden rounded-lg bg-emerald-400 px-4 py-1.5 text-sm font-bold text-slate-950 transition-colors hover:bg-emerald-300 md:block">{t("getStarted")}</Link>
          <button className="text-slate-200 md:hidden" onClick={() => setOpen(!open)} data-testid="mobile-menu-btn" aria-label="Menu">
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>
      {open && (
        <div className="border-t border-slate-800 px-5 py-4 md:hidden" data-testid="mobile-menu">
          <div className="flex flex-col gap-3">
            {links.map(([href, label]) => (
              <a key={href} href={href} onClick={() => setOpen(false)} className="text-sm text-slate-300">{label}</a>
            ))}
            <Link to="/login" data-testid="mobile-login-btn" className="rounded-xl border border-slate-700 py-2.5 text-center text-sm font-medium text-white">{t("login")}</Link>
            <Link to="/register" data-testid="mobile-register-btn" className="rounded-xl bg-emerald-400 py-2.5 text-center text-sm font-bold text-slate-950">{t("getStarted")}</Link>
          </div>
        </div>
      )}
    </header>
  );
}

function Hero({ t, lang, features }) {
  const [head, ...rest] = t("heroTitle").split(", ");
  const stats = [
    { icon: Dumbbell, v: "1.300+", l: lang === "id" ? "latihan" : "exercises" },
    { icon: Gauge, v: "15+", l: lang === "id" ? "metrik tubuh" : "body metrics" },
    { icon: Sparkles, v: "AI", l: "Coach" },
  ];
  return (
    <section className="relative overflow-hidden bg-slate-950">
      <div aria-hidden className="pointer-events-none absolute inset-0 opacity-10" style={GRID_BG} />
      <div aria-hidden className="pointer-events-none absolute -left-40 top-1/3 h-96 w-96 rounded-full bg-emerald-400/10 blur-3xl" />
      <div className="relative z-10 mx-auto grid max-w-7xl grid-cols-1 items-center gap-14 px-5 pb-20 pt-16 lg:grid-cols-2 lg:gap-16 lg:pb-28 lg:pt-24">
        <div>
          <motion.span {...fadeUp()} data-testid="hero-badge"
            className="inline-flex items-center gap-2 rounded-full border border-emerald-400/20 bg-emerald-400/10 px-4 py-1.5 text-sm font-medium text-emerald-400">
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-400" />
            </span>
            {lang === "id" ? "Kebugaran & komposisi tubuh, elegan" : "Fitness & body comp, elegant"}
          </motion.span>
          <motion.h1 {...fadeUp(0.05)} data-testid="hero-title"
            className="mt-6 font-display text-4xl font-extrabold leading-[1.05] tracking-tight text-white sm:text-5xl lg:text-6xl">
            {head}{rest.length > 0 && ","} {rest.length > 0 && <span className="text-emerald-400">{rest.join(", ")}</span>}
          </motion.h1>
          <motion.p {...fadeUp(0.1)} className="mt-6 max-w-xl text-base leading-relaxed text-slate-300 sm:text-lg">{t("heroSub")}</motion.p>
          <motion.div {...fadeUp(0.15)} className="mt-8 flex flex-col gap-3 sm:flex-row">
            <PrimaryCta testid="hero-cta-btn">{t("getStarted")}</PrimaryCta>
            <Link to="/login" data-testid="hero-login-btn"
              className="inline-flex items-center justify-center rounded-xl border border-slate-700 px-6 py-3.5 font-semibold text-white transition-colors hover:border-emerald-400/50 hover:bg-slate-900">
              {t("login")}
            </Link>
          </motion.div>
          <motion.div {...fadeUp(0.2)} className="mt-8 flex flex-wrap gap-6 sm:gap-8" data-testid="hero-stats">
            {stats.map((s) => (
              <div key={s.l} className="flex items-center gap-1.5">
                <s.icon className="h-4 w-4 text-emerald-400" />
                <span className="text-sm font-bold text-white tabular-nums">{s.v}</span>
                <span className="text-sm text-slate-400">{s.l}</span>
              </div>
            ))}
          </motion.div>
          <div className="mt-8 flex flex-wrap gap-1.5" data-testid="hero-feature-chips">
            {features.map((f, i) => (
              <motion.a key={f.title} href="#features" {...fadeUp(0.25 + i * 0.04)}
                className="rounded-full border border-emerald-400/20 bg-slate-950 px-3 py-1 text-sm text-slate-300 transition-colors hover:border-emerald-400/50 hover:text-white">
                {f.title}
              </motion.a>
            ))}
          </div>
        </div>
        <div className="flex justify-center lg:justify-end"><PhoneMockup lang={lang} /></div>
      </div>
    </section>
  );
}

function Features({ t, lang, features }) {
  return (
    <SectionShell id="features">
      <SectionHead testid="features-heading" label={t("features")}
        title={lang === "id" ? "Semua yang kamu butuhkan untuk" : "Everything you need to"}
        accent={lang === "id" ? "progres nyata" : "see real progress"}
        sub={t("heroSub")} />
      <div className="mt-16 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {features.map((f, i) => (
          <motion.div key={f.title} {...fadeUp(i * 0.05)} data-testid={`feature-card-${i}`}
            className="group h-full rounded-2xl border border-slate-800 bg-slate-950 p-6 transition-[border-color,background-color,transform] duration-300 hover:-translate-y-1 hover:border-emerald-400/30 hover:bg-slate-900">
            <div className="grid h-12 w-12 place-items-center rounded-xl bg-emerald-400/10 text-emerald-400 transition-colors group-hover:bg-emerald-400/20">
              <f.icon className="h-6 w-6" />
            </div>
            <h3 className="mt-4 font-display text-lg font-bold text-white">{f.title}</h3>
            <p className="mt-2 text-sm leading-relaxed text-slate-400">{f.desc}</p>
          </motion.div>
        ))}
      </div>
    </SectionShell>
  );
}

function HowItWorks({ t, lang, steps }) {
  return (
    <SectionShell id="how">
      <SectionHead testid="how-heading" label={t("howItWorks")}
        title={lang === "id" ? "Mulai dalam" : "Get started in"} accent={lang === "id" ? "3 langkah" : "3 steps"} />
      <div className="mt-16 grid gap-6 md:grid-cols-3">
        {steps.map((s, i) => (
          <motion.div key={s.n} {...fadeUp(i * 0.08)} data-testid={`how-step-${i}`}
            className="relative rounded-2xl border border-slate-800 bg-slate-950 p-6 transition-colors duration-300 hover:border-emerald-400/30">
            <span className="font-display text-5xl font-extrabold text-emerald-400/25 tabular-nums">{s.n}</span>
            <h3 className="mt-3 font-display text-xl font-bold text-white">{s.title}</h3>
            <p className="mt-2 text-sm text-slate-400">{s.desc}</p>
          </motion.div>
        ))}
      </div>
    </SectionShell>
  );
}

function Faq({ t, lang, faqs }) {
  return (
    <SectionShell id="faq">
      <div className="mx-auto max-w-3xl">
        <SectionHead testid="faq-heading" label={t("faq")}
          title={lang === "id" ? "Pertanyaan yang" : "Frequently asked"} accent={lang === "id" ? "sering ditanyakan" : "questions"} />
        <motion.div {...fadeUp(0.1)}>
          <Accordion type="single" collapsible className="mt-12">
            {faqs.map((f, i) => (
              <AccordionItem key={i} value={`faq-${i}`} data-testid={`faq-item-${i}`} className="border-b border-slate-800">
                <AccordionTrigger className="py-5 text-left font-display font-semibold text-white hover:text-emerald-400 hover:no-underline">{f.q}</AccordionTrigger>
                <AccordionContent className="text-sm leading-relaxed text-slate-400">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </motion.div>
      </div>
    </SectionShell>
  );
}

function FinalCta({ t, lang }) {
  return (
    <section className="relative overflow-hidden bg-slate-950 py-24 lg:py-32">
      <div aria-hidden className="absolute left-1/2 top-0 h-[500px] w-[800px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-emerald-400/10 blur-3xl" />
      <div aria-hidden className="pointer-events-none absolute inset-0 opacity-10" style={GRID_BG} />
      <div aria-hidden className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-slate-700 to-transparent" />
      <div className="relative z-10 mx-auto max-w-3xl px-5 text-center">
        <motion.div {...fadeUp()} className="mx-auto grid h-14 w-14 place-items-center rounded-2xl bg-emerald-400/10 text-emerald-400"><Zap className="h-7 w-7" /></motion.div>
        <motion.h2 {...fadeUp(0.05)} className="mt-6 font-display text-3xl font-extrabold tracking-tight text-white sm:text-4xl lg:text-5xl">
          {lang === "id" ? <>Mulai kebiasaan yang <span className="text-emerald-400">mengubah fisikmu</span></> : <>Start the habit that <span className="text-emerald-400">transforms your physique</span></>}
        </motion.h2>
        <motion.p {...fadeUp(0.1)} className="mx-auto mt-6 max-w-lg text-base text-slate-400 md:text-lg">{lang === "id" ? "Gratis untuk memulai. Tanpa kartu kredit." : "Free to start. No credit card."}</motion.p>
        <motion.div {...fadeUp(0.15)} className="mt-10"><PrimaryCta testid="cta-bottom-btn">{t("getStarted")}</PrimaryCta></motion.div>
      </div>
    </section>
  );
}

export default function Landing() {
  const { t, lang, setLang } = useI18n();
  const id = lang === "id";

  useDarkBody();
  const { user } = useAuth();
  if (user && isInstalledApp()) return <Navigate to="/dashboard" replace />;

  const features = [
    { icon: CalendarCheck, title: id ? "Check-in Harian" : "Daily Check-in", desc: id ? "Satu ketuk untuk check-in dengan mood, tidur, dan nyeri otot. Bangun streak yang konsisten." : "One-tap check-in with mood, sleep, and soreness. Build a consistent streak." },
    { icon: Dumbbell, title: id ? "Catat Latihan" : "Log Workouts", desc: id ? "Sesi live dengan timer istirahat, RPE, dan deteksi PR otomatis." : "Live sessions with rest timer, RPE, and automatic PR detection." },
    { icon: Activity, title: id ? "BMI & Komposisi Tubuh" : "BMI & Body Comp", desc: id ? "Berat, BMI, body fat, lingkar tubuh, dan massa otot — semua bisa dijelaskan." : "Weight, BMI, body fat, circumferences, and lean mass — all explainable." },
    { icon: Camera, title: id ? "Foto Progres" : "Progress Photos", desc: id ? "Slider sebelum/sesudah untuk melihat transformasi nyata dari waktu ke waktu." : "Before/after slider to see real transformation over time." },
    { icon: LineChart, title: id ? "Grafik & Streak" : "Charts & Streaks", desc: id ? "Tren yang mulus, heatmap konsistensi, dan estimasi 1RM." : "Smooth trends, consistency heatmap, and estimated 1RM." },
    { icon: Sparkles, title: "AI Coach", desc: id ? "Saran cerdas yang dipersonalisasi berdasarkan data latihan & tubuhmu." : "Smart, personalized suggestions based on your training & body data." },
  ];

  const steps = [
    { n: "01", title: id ? "Atur profil" : "Set your profile", desc: id ? "Tinggi, berat, tujuan, dan tingkat pengalaman." : "Height, weight, goals, and experience level." },
    { n: "02", title: id ? "Check-in & latih" : "Check in & train", desc: id ? "Catat sesimu dengan timer dan deteksi PR." : "Log your sessions with timer and PR detection." },
    { n: "03", title: id ? "Pantau progres" : "Track progress", desc: id ? "Lihat tren tubuh, kekuatan, dan konsistensi." : "See body, strength, and consistency trends." },
  ];

  const faqs = id ? [
    { q: "Apakah FitBersama gratis?", a: "Ya, FitBersama 100% gratis. Semua fitur — check-in, catatan latihan, metrik tubuh, AI Coach, laporan mingguan, hingga obrolan komunitas — bisa dipakai tanpa biaya." },
    { q: "Apakah BMI akurat?", a: "BMI adalah estimasi cepat dan memiliki keterbatasan (mis. atlet dengan otot besar). Kami menampilkannya bersama body fat untuk konteks yang lebih baik." },
    { q: "Bisakah saya ekspor data saya?", a: "Tentu. Kamu bisa mengekspor semua data dalam format JSON kapan saja dari Pengaturan." },
  ] : [
    { q: "Is FitBersama free?", a: "Yes, FitBersama is 100% free. Every feature — check-ins, workout logging, body metrics, AI Coach, weekly reports, and community chat — is available at no cost." },
    { q: "Is BMI accurate?", a: "BMI is a quick estimate with limits (e.g. muscular athletes). We show it alongside body fat for better context." },
    { q: "Can I export my data?", a: "Absolutely. You can export all your data as JSON anytime from Settings." },
  ];

  return (
    <div className="dark min-h-screen bg-slate-950 text-slate-100" data-testid="landing-page">
      <NavBar t={t} lang={lang} setLang={setLang} />
      <Hero t={t} lang={lang} features={features} />
      <Features t={t} lang={lang} features={features} />
      <AppPreview lang={lang} />
      <HowItWorks t={t} lang={lang} steps={steps} />
      <Faq t={t} lang={lang} faqs={faqs} />
      <FinalCta t={t} lang={lang} />
      <LandingFooter />
    </div>
  );
}
